import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAccessLinkInfo, redeemAccessLink } from '../api/accessLinks';
import Button from '../components/Button';
import styles from './Redeem.module.css';

const REDEEM_TOKEN_KEY = 'pendingRedeemToken';

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const Redeem = () => {
  const { token: linkToken } = useParams();
  const navigate = useNavigate();
  const { token: authToken, isAuthenticated } = useAuth();

  const [linkInfo, setLinkInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    document.title = 'Активация доступа — Терушкин ОС';
  }, []);

  // Если пользователь не авторизован — сохраняем токен и отправляем на /login
  useEffect(() => {
    if (!isAuthenticated && linkToken) {
      sessionStorage.setItem(REDEEM_TOKEN_KEY, linkToken);
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, linkToken, navigate]);

  // Загружаем информацию о ссылке (валидна / истекла / использована)
  useEffect(() => {
    if (!linkToken || !isAuthenticated) return;

    setIsLoading(true);
    getAccessLinkInfo(linkToken)
      .then((data) => {
        setLinkInfo(data.link);
      })
      .catch((err) => {
        const status = err.response?.data?.status || 'error';
        const message = err.response?.data?.message || 'Не удалось загрузить ссылку';
        setError({ status, message });
      })
      .finally(() => setIsLoading(false));
  }, [linkToken, isAuthenticated]);

  const handleRedeem = async () => {
    setIsRedeeming(true);
    setError(null);
    try {
      const data = await redeemAccessLink(authToken, linkToken);
      setSuccess({ pay_until: data.pay_until });
      // После успешной активации очищаем сохранённый токен
      sessionStorage.removeItem(REDEEM_TOKEN_KEY);
    } catch (err) {
      const status = err.response?.data?.status || 'error';
      const message = err.response?.data?.message || 'Не удалось активировать доступ';
      const payUntil = err.response?.data?.pay_until || null;
      setError({ status, message, pay_until: payUntil });
    } finally {
      setIsRedeeming(false);
    }
  };

  // Авторедирект после успеха
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => navigate('/news'), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  if (!isAuthenticated) return null;

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <p className={styles.loading}>Проверяем ссылку...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <div className={styles.successIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h1 className={styles.title}>Доступ активирован!</h1>
          <p className={styles.message}>
            Ваша подписка действует до <strong>{formatDate(success.pay_until)}</strong>.
            <br />
            Перенаправляем вас в новости...
          </p>
          <Link to="/news">
            <Button variant="primary" size="md">Перейти в новости</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    const isAlreadySubscribed = error.status === 'already_subscribed';
    const titleByStatus = {
      already_subscribed: 'У вас уже активна подписка',
      exhausted: 'Лимит активаций исчерпан',
      expired: 'Срок действия ссылки истёк',
      not_found: 'Ссылка не найдена',
    };
    const title = titleByStatus[error.status] || 'Ссылка недоступна';
    return (
      <div className={styles.page}>
        <div className={styles.inner}>
          <div className={styles.errorIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.message}>
            {error.message}
            {isAlreadySubscribed && error.pay_until && (
              <>
                <br />
                Действует до <strong>{formatDate(error.pay_until)}</strong>.
              </>
            )}
          </p>
          <div className={styles.actions}>
            <Link to={isAlreadySubscribed ? '/news' : '/'}>
              <Button variant="primary" size="md">
                {isAlreadySubscribed ? 'Перейти в новости' : 'На главную'}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!linkInfo) return null;

  const previewDate = formatDate(linkInfo.preview_pay_until);
  const isMultiUse = linkInfo.max_uses == null || linkInfo.max_uses > 1;
  const remainingUses = linkInfo.max_uses != null
    ? Math.max(0, linkInfo.max_uses - linkInfo.uses_count)
    : null;

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <h1 className={styles.title}>Активация доступа</h1>
        <p className={styles.subtitle}>
          {isMultiUse
            ? 'Вам предоставлена ссылка для получения доступа к закрытым материалам клуба.'
            : 'Вам предоставлена одноразовая ссылка для получения доступа к закрытым материалам клуба.'}
        </p>

        <div className={styles.card}>
          <div className={styles.cardLabel}>Доступ будет активен до</div>
          <div className={styles.cardDate}>{previewDate}</div>
          {linkInfo.mode === 'duration' && linkInfo.duration_days && (
            <div className={styles.cardHint}>
              Длительность: {linkInfo.duration_days} {linkInfo.duration_days === 1 ? 'день' : 'дн.'}
            </div>
          )}
          {remainingUses != null && (
            <div className={styles.cardHint}>
              Осталось активаций: {remainingUses} из {linkInfo.max_uses}
            </div>
          )}
        </div>

        <Button
          variant="primary"
          size="lg"
          className={styles.redeemButton}
          onClick={handleRedeem}
          disabled={isRedeeming}
        >
          {isRedeeming ? 'Активация...' : 'Активировать доступ'}
        </Button>

        <p className={styles.note}>
          {linkInfo.max_uses === 1
            ? 'Нажимая «Активировать», вы соглашаетесь с условиями участия в клубе. После активации ссылка станет недействительной.'
            : 'Нажимая «Активировать», вы соглашаетесь с условиями участия в клубе.'}
        </p>
      </div>
    </div>
  );
};

export default Redeem;
export { REDEEM_TOKEN_KEY };
